import { createRemoteJWKSet, jwtVerify, JWTPayload } from 'jose';

const getIssuer = () => {
  const iss = 'https://sso.acesso.gov.br';
  return String(iss).replace(/\/+$/, '');
};

const fetchOpenIdConfig = async (issuer: string) => {
  const url = `${issuer}/.well-known/openid-configuration`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Falha OpenID config: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Erro ao buscar configuração OpenID:', error);
    throw error;
  }
};

let _jwks: ReturnType<typeof createRemoteJWKSet> | null = null;

const getRemoteJwks = async () => {
  if (_jwks) return _jwks;

  try {
    const issuer = getIssuer();
    const cfg = await fetchOpenIdConfig(issuer);

    if (!cfg.jwks_uri) {
      throw new Error('jwks_uri ausente na configuração OpenID');
    }

    _jwks = createRemoteJWKSet(new URL(cfg.jwks_uri));
    return _jwks;
  } catch (error) {
    console.error('Erro ao obter JWKS:', error);
    throw error;
  }
};

export type ValidatedToken = {
  payload: JWTPayload & {
    email?: string;
    name?: string;
    nome?: string;
    sub?: string | number;
    papel?: string | string[];
    permissoes?: any[];
  };
  raw: string;
};

export async function validateGovbrAccessToken(
  token: string,
  opts?: { audience?: string | string[] },
): Promise<ValidatedToken> {
  try {
    const issuer = getIssuer();
    const JWKS = await getRemoteJwks();

    const { payload } = await jwtVerify(token, JWKS, {
      issuer,
      audience: opts?.audience,
    });

    return { payload, raw: token };
  } catch (error) {
    console.error('Erro na validação do token GovBR:', error);
    throw error;
  }
}

const safeAtob = (str: string): string => {
  try {
    const paddedStr = str.padEnd(
      str.length + ((4 - (str.length % 4)) % 4),
      '=',
    );
    return atob(paddedStr.replace(/-/g, '+').replace(/_/g, '/'));
  } catch (error) {
    console.error('Erro ao decodificar base64:', error);
    throw error;
  }
};

export function decodeJwtUnsafe<T = any>(token: string): T | null {
  try {
    const [hdrB64, payloadB64] = token.split('.');
    if (!hdrB64 || !payloadB64) {
      console.error('Token JWT inválido: formato incorreto');
      return null;
    }

    const json = safeAtob(payloadB64);
    const payload = JSON.parse(json);
    /*
    console.log('Token decodificado (unsafe):', {
      sub: payload.sub,
      exp: payload.exp,
      papel: payload.papel,
    });
*/
    return payload;
  } catch (error) {
    // console.error('Erro ao decodificar token JWT:', error);
    return null;
  }
}

export const detectTokenType = (
  token: string,
): 'local' | 'govbr' | 'unknown' => {
  try {
    const [headerB64] = token.split('.');
    if (!headerB64) return 'unknown';

    const paddedHeader = headerB64.padEnd(
      headerB64.length + ((4 - (headerB64.length % 4)) % 4),
      '=',
    );
    const headerJson = atob(paddedHeader.replace(/-/g, '+').replace(/_/g, '/'));
    const header = JSON.parse(headerJson);

    const alg = header?.alg;

    if (alg && /^HS\d{3}$/i.test(alg)) {
      return 'local';
    }

    if (alg && (/^(RS|ES|PS)\d{3}$/i.test(alg) || /^EdDSA$/i.test(alg))) {
      return 'govbr';
    }

    return 'unknown';
  } catch (error) {
    console.error('Erro ao detectar tipo do token:', error);
    return 'unknown';
  }
};

export const validateLocalToken = (token: string): any => {
  try {
    const payload = decodeJwtUnsafe(token);
    if (!payload || !payload.sub) {
      throw new Error('Payload inválido');
    }

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp <= now) {
      throw new Error('Token expirado');
    }

    return payload;
  } catch (error) {
    console.error('Erro na validação do token local:', error);
    throw error;
  }
};
